import { useEffect, useId, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useKatottgSearch,
  useKatottgUnit,
  useTaxOffice,
  useTaxOfficeSearch,
  type KatottgCategory,
  type KatottgRef,
  type KatottgUnit,
  type ReferenceCode,
  type TaxOffice,
} from '@/hooks/use-fiscal-registration';
import { useT } from '@/i18n/use-t';

/**
 * PFC-11 — searchable pickers over the versioned reference data: DPS tax
 * offices for a legal entity, the KATOTTG codifier for a taxation object,
 * and a filter for the long catalogue of object types. Each picker only
 * proposes values; the code field next to it stays editable, and the server
 * checks whatever is saved against the same data.
 *
 * The names are DPS / ministry reference data and are shown as published in
 * every locale.
 */

/** The value, once it has stopped changing for `ms`. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * Search folding, the same rules as the server's `foldUa`: NFC, Ukrainian
 * lower case, apostrophes of every form dropped, Latin «i» read as «і».
 */
export function foldUa(text: string): string {
  return text
    .normalize('NFC')
    .toLocaleLowerCase('uk')
    .replace(/['’ʼ‘`´ʹ′]/g, '')
    .replace(/i/g, 'і')
    .replace(/\s+/g, ' ')
    .trim();
}

export const KATOTTG_CATEGORY_LABEL_KEY: Record<KatottgCategory, string> = {
  O: 'fiscalCabinet.katottg.category.O',
  K: 'fiscalCabinet.katottg.category.K',
  P: 'fiscalCabinet.katottg.category.P',
  H: 'fiscalCabinet.katottg.category.H',
  M: 'fiscalCabinet.katottg.category.M',
  X: 'fiscalCabinet.katottg.category.X',
  C: 'fiscalCabinet.katottg.category.C',
  B: 'fiscalCabinet.katottg.category.B',
};
export const KATOTTG_CATEGORY_LABEL: Record<KatottgCategory, string> = {
  O: 'Region',
  K: 'City with special status',
  P: 'District',
  H: 'Territorial community',
  M: 'City',
  X: 'Settlement (selyshche)',
  C: 'Village',
  B: 'City district',
};

function MoreMatches({ shown, total }: { shown: number; total: number }) {
  const t = useT('admin_ui');
  if (total <= shown) return null;
  return (
    <p className="px-2 py-1 text-xs text-muted-foreground">
      {t(
        'fiscalCabinet.reference.moreMatches',
        'Showing {shown} of {total}. Type more to narrow the search.',
      )
        .replace('{shown}', String(shown))
        .replace('{total}', String(total))}
    </p>
  );
}

function NoMatches() {
  const t = useT('admin_ui');
  return (
    <p className="px-2 py-1 text-xs text-muted-foreground">
      {t('fiscalCabinet.reference.noMatches', 'Nothing found.')}
    </p>
  );
}

// ------------------------------------------------------------------
// Tax office
// ------------------------------------------------------------------

/**
 * Search the DPS office directory and fill the legal entity's office code and
 * name. Under the search: whether the code in the form is in the directory.
 */
export function TaxOfficePicker({
  code,
  onPick,
}: {
  code: string;
  onPick: (office: TaxOffice) => void;
}) {
  const t = useT('admin_ui');
  const id = useId();
  const [query, setQuery] = useState('');
  const settled = useDebounced(query);
  const search = useTaxOfficeSearch(settled);
  const current = useTaxOffice(code.trim());
  const listId = `${id}-offices`;
  const open = query.trim() !== '' && search.data !== undefined;

  return (
    <div className="space-y-1">
      <Label htmlFor={`${id}-q`}>
        {t('fiscalCabinet.reference.taxOfficeSearch', 'Find the tax office')}
      </Label>
      <Input
        id={`${id}-q`}
        aria-controls={listId}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t(
          'fiscalCabinet.reference.taxOfficePlaceholder',
          'Code or name, e.g. 2650 or Київ',
        )}
      />
      {open && search.data !== undefined && (
        <div className="rounded-md border border-border bg-background">
          <ul id={listId} className="max-h-64 overflow-y-auto">
            {search.data.items.map((office) => (
              <li key={office.code}>
                <button
                  type="button"
                  className="w-full px-2 py-1 text-left text-xs hover:bg-muted"
                  onClick={() => {
                    onPick(office);
                    setQuery('');
                  }}
                >
                  <span className="font-mono">{office.code}</span> {office.name}
                  {office.districtName !== '' && (
                    <span className="text-muted-foreground"> · {office.districtName}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {search.data.items.length === 0 && <NoMatches />}
          <MoreMatches shown={search.data.items.length} total={search.data.total} />
        </div>
      )}
      {current.data === null && (
        <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
          {t(
            'fiscalCabinet.reference.taxOfficeUnknown',
            'This code is not in the current DPS directory of tax offices. Check it before filing.',
          )}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {t(
          'fiscalCabinet.reference.taxOfficeHelp',
          "Choose the office this taxpayer is actually registered with — it is on the taxpayer's card in the DPS electronic cabinet. The tax service refuses a filing addressed to any other office, even a valid one in the same city.",
        )}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------
// KATOTTG
// ------------------------------------------------------------------

/**
 * A taxation object sits in a settlement: a city, selyshche or village, a
 * city district, or Kyiv / Sevastopol. A region, district or community is
 * opened instead of picked.
 */
export function isPickable(unit: KatottgRef): boolean {
  return ['K', 'M', 'X', 'C', 'B'].includes(unit.category);
}

function pathLabel(path: readonly KatottgRef[]): string {
  return path.map((unit) => unit.name).join(' › ');
}

/**
 * Search the KATOTTG codifier by name or code, or browse it region →
 * district → community → settlement. Picking a settlement or city district
 * fills the code and the address's region, district and settlement.
 */
export function KatottgPicker({
  code,
  onPick,
}: {
  code: string;
  onPick: (unit: KatottgUnit) => void;
}) {
  const t = useT('admin_ui');
  const id = useId();
  const [query, setQuery] = useState('');
  const [browsing, setBrowsing] = useState(false);
  const [trail, setTrail] = useState<KatottgRef[]>([]);
  const settled = useDebounced(query);
  const parent = trail.length === 0 ? null : (trail[trail.length - 1]?.code ?? null);
  // Both the typed and the settled query: clearing the box hides results at
  // once instead of showing the last search until the debounce catches up.
  const searching = query.trim() !== '' && settled.trim() !== '';
  // While browsing, a query searches inside the unit on screen (a community
  // can have more settlements than one page shows).
  const list = useKatottgSearch(
    searching ? settled : '',
    browsing ? parent : null,
    searching || browsing,
  );
  const current = useKatottgUnit(code.trim());
  const listId = `${id}-units`;
  const open = (searching || browsing) && list.data !== undefined;

  const openUnit = (unit: KatottgUnit) => {
    setQuery('');
    setBrowsing(true);
    setTrail([...unit.path, unit]);
  };

  const pick = (unit: KatottgUnit) => {
    onPick(unit);
    setQuery('');
    setBrowsing(false);
    setTrail([]);
  };

  return (
    <div className="space-y-1">
      <Label htmlFor={`${id}-q`}>
        {t('fiscalCabinet.reference.katottgSearch', 'Find in KATOTTG')}
      </Label>
      <div className="flex gap-2">
        <Input
          id={`${id}-q`}
          aria-controls={listId}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(
            'fiscalCabinet.reference.katottgPlaceholder',
            'Settlement name or code, e.g. Київ Солом’янський',
          )}
        />
        <button
          type="button"
          className="shrink-0 rounded-md border border-border px-3 text-xs hover:bg-muted"
          onClick={() => {
            setQuery('');
            setBrowsing((value) => !value);
            setTrail([]);
          }}
          aria-pressed={browsing}
        >
          {t('fiscalCabinet.reference.browse', 'Browse')}
        </button>
      </div>
      {open && list.data !== undefined && (
        <div className="rounded-md border border-border bg-background">
          {browsing && (
            <nav className="flex flex-wrap items-center gap-1 px-2 py-1 text-xs">
              <button type="button" className="underline" onClick={() => setTrail([])}>
                {t('fiscalCabinet.reference.browseTop', 'All regions')}
              </button>
              {trail.map((unit, index) => (
                <span key={unit.code}>
                  {' › '}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setTrail(trail.slice(0, index + 1))}
                  >
                    {unit.name}
                  </button>
                </span>
              ))}
            </nav>
          )}
          <ul id={listId} className="max-h-72 overflow-y-auto">
            {list.data.items.map((unit) => (
              <li key={unit.code} className="flex items-center gap-1">
                <button
                  type="button"
                  className="min-w-0 flex-1 px-2 py-1 text-left text-xs hover:bg-muted"
                  onClick={() => (isPickable(unit) ? pick(unit) : openUnit(unit))}
                >
                  <span className="font-medium">{unit.name}</span>{' '}
                  <span className="text-muted-foreground">
                    {t(
                      KATOTTG_CATEGORY_LABEL_KEY[unit.category],
                      KATOTTG_CATEGORY_LABEL[unit.category],
                    )}
                    {unit.path.length > 0 && ` · ${pathLabel(unit.path)}`}
                  </span>{' '}
                  <span className="font-mono text-muted-foreground">{unit.code}</span>
                </button>
                {unit.hasChildren && isPickable(unit) && (
                  <button
                    type="button"
                    className="shrink-0 px-2 py-1 text-xs hover:bg-muted"
                    aria-label={t(
                      'fiscalCabinet.reference.open',
                      'Show the units inside {name}',
                    ).replace('{name}', unit.name)}
                    onClick={() => openUnit(unit)}
                  >
                    ›
                  </button>
                )}
              </li>
            ))}
          </ul>
          {list.data.items.length === 0 && <NoMatches />}
          <MoreMatches shown={list.data.items.length} total={list.data.total} />
        </div>
      )}
      {current.data !== undefined && current.data !== null && (
        <p className="text-xs text-muted-foreground">
          {pathLabel([...current.data.unit.path, current.data.unit])}
        </p>
      )}
      {current.data === null && (
        <p role="status" className="text-xs text-amber-700 dark:text-amber-400">
          {t(
            'fiscalCabinet.reference.katottgUnknown',
            'This code is not in the current KATOTTG codifier. Check it before filing.',
          )}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {t(
          'fiscalCabinet.reference.katottgHelp',
          'Choosing a settlement or a city district fills the code and the region, district and settlement of the address.',
        )}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------
// Object types
// ------------------------------------------------------------------

/** The catalogue entries matching `filter` (code or name), always keeping `current`. */
export function filterObjectTypes(
  types: readonly ReferenceCode<number>[],
  filter: string,
  current: number | null,
): ReferenceCode<number>[] {
  const folded = foldUa(filter);
  if (folded === '') return [...types];
  const tokens = folded.split(' ');
  return types.filter((type) => {
    if (type.code === current) return true;
    const haystack = `${type.code} ${foldUa(type.name)}`;
    return tokens.every((token) => haystack.includes(token));
  });
}
